import { useEffect, useMemo, useState } from 'react';
import {
  ComboBOGOData,
  ComboOptions,
  ComboSelection,
  ComboStep,
  ComboVariant,
  comboProductData,
  findVariantByOptions,
  flattenComboGroups,
  getProductOptions,
  hasAnyInStockVariantFor,
  isVariantAvailable,
  stripVariantGid,
} from './combo';
import { formatMoney, thumbLetter } from './catalog';

interface PendingSelection {
  groupType: 'buy' | 'get';
  groupIndex: number;
  itemIndex: number;
  variantId: string;
  productId: string;
  options: ComboOptions;
}

export interface ComboWizard {
  steps: ComboStep[];
  currentStep: number;
  isLastStep: boolean;
  /** Current step has a resolved variant (finalised or pending). */
  canAdvance: boolean;
  /** Every step has a finalised selection — ready to add to cart. */
  complete: boolean;
  back: () => void;
  next: () => void;
  /** Cart lines for the finished selection; empty until `complete`. */
  lines: Array<{ variantId: string; quantity: number }>;
  // internals consumed by <ComboSteps>
  selectedItems: ComboSelection[];
  pending: PendingSelection | null;
  selectedOptions: Map<string, ComboOptions>;
  carouselIndex: Map<string, number>;
  setCarouselProduct: (step: ComboStep, index: number) => void;
  selectOption: (
    step: ComboStep,
    itemIndex: number,
    productId: string,
    optionName: string,
    optionValue: string
  ) => void;
}

const stepKey = (step: ComboStep) => `${step.type}-${step.index}`;
const productKey = (productId: string) => `product-${productId}`;

/**
 * Step-per-group combo wizard. Mirrors the storefront PDP selector
 * (`shopify-bundle-product/components/ProductSelector`) so both flows behave
 * identically; only the markup is re-skinned for the live player.
 */
export function useComboWizard(data: ComboBOGOData | null): ComboWizard {
  const steps = useMemo(() => (data ? flattenComboGroups(data) : []), [data]);
  const [selectedItems, setSelectedItems] = useState<ComboSelection[]>([]);
  const [pending, setPending] = useState<PendingSelection | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Map<string, ComboOptions>>(new Map());
  const [currentStep, setCurrentStep] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState<Map<string, number>>(new Map());

  // Reset everything when the underlying rule changes
  useEffect(() => {
    setSelectedItems([]);
    setPending(null);
    setSelectedOptions(new Map());
    setCurrentStep(0);
    setCarouselIndex(new Map());
  }, [data?.discount_id]);

  const finalizedFor = (step: ComboStep | undefined) =>
    step
      ? selectedItems.find((s) => s.groupType === step.type && s.groupIndex === step.index)
      : undefined;

  /**
   * Drop a step's finalised entry. Needed whenever the shopper edits a step that
   * was already resolved (the last step auto-finalises), otherwise `lines` would
   * keep the stale variant while the UI shows the new one.
   */
  const dropFinalized = (step: ComboStep) => {
    setSelectedItems((prev) =>
      prev.filter((s) => !(s.groupType === step.type && s.groupIndex === step.index))
    );
  };

  const clearProductOptions = (productId: string) => {
    setSelectedOptions((prev) => {
      const next = new Map(prev);
      next.set(productKey(productId), {});
      return next;
    });
    setPending(null);
  };

  const setCarouselProduct = (step: ComboStep, index: number) => {
    const key = stepKey(step);
    if ((carouselIndex.get(key) || 0) === index) return;
    setCarouselIndex((prev) => new Map(prev).set(key, index));
    // Swiping to another product abandons the in-progress choice
    dropFinalized(step);
    const productId = comboProductData(step.items[index])?.id;
    if (productId) clearProductOptions(productId);
    else setPending(null);
  };

  const selectOption = (
    step: ComboStep,
    itemIndex: number,
    productId: string,
    optionName: string,
    optionValue: string
  ) => {
    const key = productKey(productId);
    const current = selectedOptions.get(key) || {};
    const nextOptions: ComboOptions = { ...current };
    if (!optionValue) delete nextOptions[optionName];
    else nextOptions[optionName] = optionValue;

    setSelectedOptions((prev) => new Map(prev).set(key, nextOptions));
    // Re-editing a resolved step invalidates its finalised entry
    dropFinalized(step);

    if (Object.keys(nextOptions).length === 0) {
      setPending(null);
      return;
    }

    const variants = comboProductData(step.items[itemIndex])?.variants || [];
    const variant = findVariantByOptions(variants, nextOptions);
    if (!variant) {
      setPending(null);
      return;
    }
    setPending({
      groupType: step.type,
      groupIndex: step.index,
      itemIndex,
      variantId: stripVariantGid(variant.id),
      productId,
      options: nextOptions,
    });
  };

  // On the last step, finalise as soon as every option axis is chosen so the
  // Add-to-cart button becomes live without an extra tap.
  useEffect(() => {
    if (!pending) return;
    if (currentStep !== steps.length - 1) return;
    const step = steps[currentStep];
    if (!step) return;
    if (pending.groupType !== step.type || pending.groupIndex !== step.index) return;
    if (finalizedFor(step)) return;

    const variants = comboProductData(step.items[pending.itemIndex])?.variants || [];
    const options = getProductOptions(variants);
    const allChosen = options.length > 0 && options.every((o) => pending.options[o.name]);
    if (!allChosen) return;
    if (!isVariantAvailable(variantForSelection(step, pending.itemIndex, pending.variantId))) {
      return;
    }

    setSelectedItems((prev) => [
      ...prev,
      {
        groupType: pending.groupType,
        groupIndex: pending.groupIndex,
        itemIndex: pending.itemIndex,
        variantId: pending.variantId,
        quantity: 1,
      },
    ]);
    setPending(null);
  }, [pending, currentStep, steps, selectedItems]);

  const currentStepDef = steps[currentStep];
  const pendingMatchesStep =
    pending != null &&
    currentStepDef != null &&
    pending.groupType === currentStepDef.type &&
    pending.groupIndex === currentStepDef.index;

  /** A resolved variant only counts if it is actually purchasable. */
  const variantForSelection = (
    step: ComboStep | undefined,
    itemIndex: number,
    variantId: string
  ): ComboVariant | null => {
    const variants = comboProductData(step?.items[itemIndex])?.variants || [];
    return variants.find((v) => stripVariantGid(v.id) === variantId) || null;
  };

  const currentFinalized = finalizedFor(currentStepDef);
  const activeChoice = pendingMatchesStep && pending ? pending : currentFinalized;
  const canAdvance = Boolean(
    activeChoice &&
      isVariantAvailable(
        variantForSelection(currentStepDef, activeChoice.itemIndex, activeChoice.variantId)
      )
  );
  const complete =
    steps.length > 0 &&
    selectedItems.length === steps.length &&
    selectedItems.every((sel) => {
      const step = steps.find((st) => st.type === sel.groupType && st.index === sel.groupIndex);
      return isVariantAvailable(variantForSelection(step, sel.itemIndex, sel.variantId));
    });

  const next = () => {
    if (pendingMatchesStep && pending) {
      setSelectedItems((prev) => [
        ...prev,
        {
          groupType: pending.groupType,
          groupIndex: pending.groupIndex,
          itemIndex: pending.itemIndex,
          variantId: pending.variantId,
          quantity: 1,
        },
      ]);
      setPending(null);
    }
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
  };

  const back = () => {
    if (currentStep === 0) return;
    const prevStep = steps[currentStep - 1];
    const prevEntry = finalizedFor(prevStep);

    // Un-finalise only the step being returned to, so its choice is editable and
    // `next()` re-adds it rather than duplicating. Steps ahead keep their entries
    // (any edit there drops them via dropFinalized), so stepping back and forward
    // preserves what the shopper already chose.
    setSelectedItems((prev) =>
      prev.filter(
        (s) => !(prevStep && s.groupType === prevStep.type && s.groupIndex === prevStep.index)
      )
    );
    setPending(null);

    if (prevEntry && prevStep) {
      const product = comboProductData(prevStep.items[prevEntry.itemIndex]);
      const variant = (product?.variants || []).find(
        (v) => stripVariantGid(v.id) === prevEntry.variantId
      );
      if (product && variant) {
        const restored: ComboOptions = {};
        (variant.selectedOptions || []).forEach((opt) => {
          restored[opt.name] = opt.value;
        });
        setSelectedOptions((prev) => new Map(prev).set(productKey(product.id), restored));
        setPending({
          groupType: prevEntry.groupType,
          groupIndex: prevEntry.groupIndex,
          itemIndex: prevEntry.itemIndex,
          variantId: prevEntry.variantId,
          productId: product.id,
          options: restored,
        });
      }
    }

    setCurrentStep(currentStep - 1);
  };

  // Order by step, not by the order the shopper happened to finalise them, so the
  // cart lines are stable across Back / forward navigation.
  const lines = useMemo(() => {
    if (!complete) return [];
    return steps
      .map((step) =>
        selectedItems.find((s) => s.groupType === step.type && s.groupIndex === step.index)
      )
      .filter((s): s is ComboSelection => Boolean(s))
      .map((s) => ({ variantId: s.variantId, quantity: s.quantity }));
  }, [complete, selectedItems, steps]);

  return {
    steps,
    currentStep,
    isLastStep: steps.length > 0 && currentStep === steps.length - 1,
    canAdvance,
    complete,
    back,
    next,
    lines,
    selectedItems,
    pending,
    selectedOptions,
    carouselIndex,
    setCarouselProduct,
    selectOption,
  };
}

function ordinalLabel(index: number, total: number): string {
  if (index === total - 1) return 'Select your final product';
  if (index === 0) return 'Select your first product';
  if (index === 1) return 'Select your second product';
  if (index === 2) return 'Select your third product';
  const n = index + 1;
  const lastTwo = n % 100;
  const last = n % 10;
  let suffix = 'th';
  if (lastTwo < 11 || lastTwo > 13) {
    if (last === 1) suffix = 'st';
    else if (last === 2) suffix = 'nd';
    else if (last === 3) suffix = 'rd';
  }
  return `Select your ${n}${suffix} product`;
}

function resolveVariant(
  variants: ComboVariant[],
  wizard: ComboWizard,
  step: ComboStep,
  itemIndex: number,
  options: ComboOptions
): ComboVariant | null {
  const finalized = wizard.selectedItems.find(
    (s) => s.groupType === step.type && s.groupIndex === step.index
  );
  if (finalized && finalized.itemIndex === itemIndex) {
    return variants.find((v) => stripVariantGid(v.id) === finalized.variantId) || null;
  }
  const pending = wizard.pending;
  if (
    pending &&
    pending.groupType === step.type &&
    pending.groupIndex === step.index &&
    pending.itemIndex === itemIndex
  ) {
    return variants.find((v) => stripVariantGid(v.id) === pending.variantId) || null;
  }
  if (Object.keys(options).length > 0) {
    return findVariantByOptions(variants, options) || null;
  }
  return null;
}

/** The scrolling body of the wizard — footer buttons live in `il-pdp-actions`. */
export function ComboSteps({ wizard }: { wizard: ComboWizard }) {
  const { steps, currentStep } = wizard;
  const [touchStart, setTouchStart] = useState<Map<string, number>>(new Map());

  if (steps.length === 0) return null;

  return (
    <div className="il-combo">
      <div className="il-combo-progress">
        <div
          className="il-combo-progress-fill"
          style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
        />
      </div>

      <div className="il-combo-steps">
        <div
          className="il-combo-steps-track"
          style={{ transform: `translateX(-${currentStep * 100}%)` }}
        >
          {steps.map((step, stepIndex) => {
            const key = stepKey(step);
            const carousel = wizard.carouselIndex.get(key) || 0;
            const finalized = wizard.selectedItems.find(
              (s) => s.groupType === step.type && s.groupIndex === step.index
            );

            return (
              <div
                key={key}
                className={`il-combo-step${stepIndex === currentStep ? ' is-active' : ''}`}
                aria-hidden={stepIndex !== currentStep}
              >
                <div className="il-combo-step-label">
                  <span className="il-combo-step-count">
                    {stepIndex + 1}/{steps.length}
                  </span>
                  <span>{ordinalLabel(stepIndex, steps.length)}</span>
                </div>

                <div className="il-combo-carousel">
                  {step.items.length > 1 && (
                    <button
                      type="button"
                      className="il-combo-carousel-nav il-combo-carousel-prev"
                      disabled={carousel === 0}
                      aria-label="Previous product"
                      onClick={() => wizard.setCarouselProduct(step, carousel - 1)}
                    >
                      ←
                    </button>
                  )}

                  <div
                    className="il-combo-carousel-track"
                    style={{ transform: `translateX(-${carousel * 100}%)` }}
                    onTouchStart={(e) => {
                      const touch = e.touches[0];
                      if (touch) setTouchStart((prev) => new Map(prev).set(key, touch.clientX));
                    }}
                    onTouchEnd={(e) => {
                      const startX = touchStart.get(key);
                      const endTouch = e.changedTouches[0];
                      if (startX === undefined || !endTouch) return;
                      const diff = startX - endTouch.clientX;
                      if (Math.abs(diff) > 50) {
                        if (diff > 0 && carousel < step.items.length - 1) {
                          wizard.setCarouselProduct(step, carousel + 1);
                        } else if (diff < 0 && carousel > 0) {
                          wizard.setCarouselProduct(step, carousel - 1);
                        }
                      }
                      setTouchStart((prev) => new Map(prev).set(key, 0));
                    }}
                  >
                    {step.items.map((item, itemIndex) => {
                      const product = comboProductData(item);
                      if (!product) {
                        return (
                          <div key={itemIndex} className="il-combo-slide">
                            <p className="il-empty">Product unavailable</p>
                          </div>
                        );
                      }

                      const variants = product.variants || [];
                      const options = wizard.selectedOptions.get(productKey(product.id)) || {};
                      const axes = getProductOptions(variants);
                      const variant = resolveVariant(variants, wizard, step, itemIndex, options);
                      const available = variant ? isVariantAvailable(variant) : true;
                      const allChosen =
                        axes.length > 0 && axes.every((o) => options[o.name]);
                      const isSelected =
                        allChosen &&
                        (finalized?.itemIndex === itemIndex ||
                          (wizard.pending?.groupType === step.type &&
                            wizard.pending?.groupIndex === step.index &&
                            wizard.pending?.itemIndex === itemIndex));
                      const image = variant?.image || product.images?.[0]?.url || null;

                      return (
                        <div key={itemIndex} className="il-combo-slide">
                          <div
                            className={`il-combo-card${isSelected ? ' is-selected' : ''}${
                              !available ? ' is-unavailable' : ''
                            }`}
                          >
                            <div className="il-combo-card-img">
                              {image ? (
                                <img src={image} alt="" />
                              ) : (
                                <span className="il-pdp-ph">{thumbLetter(product.title)}</span>
                              )}
                              <span className="il-combo-card-title">{product.title}</span>
                              {variant?.price != null && (
                                <span className="il-combo-card-price">
                                  RM {formatMoney(variant.price)}
                                </span>
                              )}
                            </div>

                            {axes.map((axis) => (
                              <div key={axis.name} className="il-option-block">
                                <div className="il-option-label">
                                  {axis.name}
                                  {options[axis.name] ? (
                                    <span className="il-option-picked">
                                      : {options[axis.name]}
                                    </span>
                                  ) : null}
                                </div>
                                <div className="il-option-pills">
                                  {axis.values.map((value) => {
                                    const inStock = hasAnyInStockVariantFor(variants, {
                                      ...options,
                                      [axis.name]: value,
                                    });
                                    const active = options[axis.name] === value;
                                    return (
                                      <button
                                        key={value}
                                        type="button"
                                        className={`il-option-pill${active ? ' is-active' : ''}${
                                          !inStock ? ' is-disabled' : ''
                                        }`}
                                        onClick={() =>
                                          wizard.selectOption(
                                            step,
                                            itemIndex,
                                            product.id,
                                            axis.name,
                                            active ? '' : value
                                          )
                                        }
                                      >
                                        {value}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}

                            {!available && (
                              <p className="il-combo-oos">This variant is out of stock</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {step.items.length > 1 && (
                    <button
                      type="button"
                      className="il-combo-carousel-nav il-combo-carousel-next"
                      disabled={carousel >= step.items.length - 1}
                      aria-label="Next product"
                      onClick={() => wizard.setCarouselProduct(step, carousel + 1)}
                    >
                      →
                    </button>
                  )}
                </div>

                {step.items.length > 1 && (
                  <div className="il-combo-dots">
                    {step.items.map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`il-combo-dot${idx === carousel ? ' is-active' : ''}`}
                        aria-label={`Product ${idx + 1}`}
                        onClick={() => wizard.setCarouselProduct(step, idx)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
